// ZEIT-EINHEIT-PORTAL-001 — EINE Stelle, an der aus einem Server-Zeitwert Anzeige wird.
//
// Der Server liefert Zeitwerte in zwei Formaten: ISO mit `Z` (neu) und das rohe
// SQLite-Format „YYYY-MM-DD HH:MM:SS" (Altbestand). Das rohe Format hat KEINE
// Zeitzonenkennung, ist aber UTC. `new Date('2026-09-03 19:30:00')` liest es als
// ORTSzeit — die Anzeige lag damit zwei Stunden zu früh, und ab 22:00 Berlin sogar
// einen Tag daneben. Genau dieser Fehler stand an vier Stellen im Portal.
//
// Die Lösung war im Auftragsbuch (pages/OrderLog.js) schon richtig gebaut, aber nirgends
// sonst benutzt. Hier steht sie jetzt EINMAL — jede Seite ruft sie auf.
//
// Grundsatz: gerechnet wird in UTC, angezeigt wird IMMER in Europe/Berlin.

export const ZEITZONE = 'Europe/Berlin';

// Server-Zeitwert (ISO mit Kennung ODER SQLite-UTC ohne) → Date. Ungültig → null.
export function alsDatum(wert) {
  if (wert instanceof Date) return Number.isNaN(wert.getTime()) ? null : wert;
  if (wert == null) return null;
  const roh = String(wert).trim();
  if (!roh) return null;
  const kandidat = roh.includes('T') ? roh : roh.replace(' ', 'T');
  const hatZone = /(Z|[+-]\d{2}:?\d{2})$/.test(kandidat);
  const datum = new Date(hatZone ? kandidat : `${kandidat}Z`);
  return Number.isNaN(datum.getTime()) ? null : datum;
}

const teile = (wert, optionen) => {
  const datum = alsDatum(wert);
  return datum ? datum.toLocaleString('de-DE', { timeZone: ZEITZONE, ...optionen }) : '—';
};

// „21:30"
export const uhrzeit = (wert) => teile(wert, { hour: '2-digit', minute: '2-digit' });

// „03.09.26, 21:30"
export const datumUndZeit = (wert) => teile(wert, {
  day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
});

// „03.09.2026"
export const nurDatum = (wert) => teile(wert, { day: '2-digit', month: '2-digit', year: 'numeric' });

// „03.09.2026, 21:30" — Abrechnungszeiträume (pages/Invoices.js)
export const datumLangUndZeit = (wert) => teile(wert, { dateStyle: 'medium', timeStyle: 'short' });

// „3. Sept., 21:30" — Postfach-Zeitstempel (pages/Inbox.js)
export const datumKurzMitZeit = (wert) => teile(wert, {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});

// „Do., 3. Sept., 21:30" — Reservierungstermine (pages/Reservations.js)
export const wochentagKurzMitZeit = (wert) => teile(wert, {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
});

// „3. September 2026" — Tagesüberschrift
export const tagUeberschrift = (wert) => teile(wert, { day: 'numeric', month: 'long', year: 'numeric' });

// „2026-09-03" nach BERLINER Kalendertag — der Schlüssel, nach dem gruppiert wird.
// `toISOString()` wäre hier falsch: es gruppiert nach UTC-Tag und zerschneidet den Abend.
export function tagSchluessel(wert) {
  const datum = alsDatum(wert);
  if (!datum) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZEITZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(datum);
  return p;
}

// Berliner Kalendertag um ganze Tage verschieben. UTC dient hier nur als neutrale
// Kalenderarithmetik auf einem bereits bestimmten JJJJ-MM-TT-Schlüssel; dadurch kann
// weder die Gerätezeitzone noch eine Sommerzeitumstellung den Wochentag verschieben.
function tagVerschieben(tag, tage) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(tag || ''));
  if (!match) return '';
  const datum = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + tage));
  return datum.toISOString().slice(0, 10);
}

// Montag bis Sonntag der BERLINER Kalenderwoche, unabhängig davon, in welcher
// Zeitzone das Gerät oder der Browser gerade läuft.
export function berlinerKalenderwoche(wert = new Date()) {
  const heute = tagSchluessel(wert);
  if (!heute) return { from: '', to: '' };
  const [jahr, monat, tag] = heute.split('-').map(Number);
  const seitMontag = (new Date(Date.UTC(jahr, monat - 1, tag)).getUTCDay() + 6) % 7;
  return {
    from: tagVerschieben(heute, -seitMontag),
    to: tagVerschieben(heute, 6 - seitMontag),
  };
}

// Operative Kennzahlen verwenden den lokalen Kalendertag, der um 04:00 beginnt.
// Der Kalenderhelfer oben bleibt für Belege, Fristen und andere echte Kalenderdaten erhalten.
export function betriebstagSchluessel(wert = new Date()) {
  const datum = alsDatum(wert);
  if (!datum) return '';
  const teile = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: ZEITZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(datum).filter((teil) => teil.type !== 'literal').map((teil) => [teil.type, teil.value]));
  const tag = `${teile.year}-${teile.month}-${teile.day}`;
  return Number(teile.hour) < 4 ? tagVerschieben(tag, -1) : tag;
}

export function berlinerBetriebswoche(wert = new Date()) {
  const heute = betriebstagSchluessel(wert);
  if (!heute) return { from: '', to: '' };
  const [jahr, monat, tag] = heute.split('-').map(Number);
  const seitMontag = (new Date(Date.UTC(jahr, monat - 1, tag)).getUTCDay() + 6) % 7;
  return {
    from: tagVerschieben(heute, -seitMontag),
    to: tagVerschieben(heute, 6 - seitMontag),
  };
}

// Abstand zwischen zwei Ereignissen, wie im Vorbild: „37 Sek." · „13 Min." · „4 Std. 30 Min."
export function dauer(sekunden) {
  const s = Math.max(0, Math.round(Number(sekunden) || 0));
  if (s < 60) return `${s} Sek.`;
  const minuten = Math.floor(s / 60);
  if (minuten < 60) return `${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  const rest = minuten % 60;
  return rest === 0 ? `${stunden} Std.` : `${stunden} Std. ${rest} Min.`;
}
